import {
  FieldPath, FieldValues, useFormContext, useWatch,
} from 'react-hook-form'
import { useMemo } from 'react'
import { FieldRule } from '../../models/fieldRules'
import { NctsPhase, NctsScope } from '../../../../shared/ncts/types'
import getDeclarationFieldRequirementsResolver from '../validations/fieldRequirements/registry'
import { DeclarationEditorData } from '../contexts/DeclarationEditorContext'
import { ServerValidationResult } from '../../models/types'

const TRANSIT_OPERATION_FIELDS_DEPS: Record<string, string[]> = {
  activeBorderTransportMeans: ['borderModeOfTransport'],
  'activeBorderTransportMeans.*.estimatedDateAndTimeOfDeparture': ['activeBorderTransportMeans.*'],
  'activeBorderTransportMeans.*.actualDateAndTimeOfDeparture': ['activeBorderTransportMeans.*'],
  authorisations: ['reducedDatasetIndicator'],
  borderModeOfTransport: ['entryIndicator'],
  'buyer.address.streetNumber': ['buyer'],
  'buyer.address.postcode': ['buyer'],
  'buyer.contactPerson.telephone': ['buyer'],
  'carrier.contactPerson': ['carrier'],
  'carrier.contactPerson.name': ['carrier'],
  'carrier.contactPerson.telephone': ['carrier'],
  'carrier.contactPerson.email': ['carrier'],
  consignee: ['specificCircumstanceIndicator'],
  'consignee.contactPerson': ['consignee'],
  'consignee.contactPerson.name': ['consignee'],
  'consignee.contactPerson.telephone': ['consignee'],
  'consignee.contactPerson.email': ['consignee'],
  'consignee.address': ['consignee'],
  'consignee.address.postcode': ['consignee'],
  'consignee.name': ['consignee'],
  'consignee.typeOfPerson': ['consignee'],
  consignor: ['reducedDatasetIndicator', 'consignor', 'specificCircumstanceIndicator'],
  'consignor.contactPerson': ['consignor'],
  'consignor.contactPerson.name': ['consignor'],
  'consignor.contactPerson.telephone': ['consignor'],
  'consignor.contactPerson.email': ['consignor'],
  'consignor.address': ['consignor'],
  'consignor.address.postcode': ['consignor'],
  'consignor.name': ['consignor'],
  'consignor.typeOfPerson': ['consignor'],
  countryOfRoutingOfConsignment: ['bindingItinerary'],
  customsOfficesOfTransitExit: ['transitCustomsOffices'],
  limitDate: ['authorisations'],
  locationOfGoods: ['departureCustomsOffice'],
  'locationOfGoods.typeOfLocation': ['locationOfGoods', 'departureCustomsOffice'],
  'locationOfGoods.address': ['locationOfGoods'],
  'locationOfGoods.additionalIdentifier': ['locationOfGoods'],
  'locationOfGoods.qualifierOfIdentification': ['locationOfGoods'],
  'locationOfGoods.postcodeAddress': ['locationOfGoods'],
  'locationOfGoods.referenceNumber': ['locationOfGoods'],
  'previousDocument.*.documentType': ['previousDocument.*'],
  'previousDocument.*.procedureType': ['previousDocument.*'],
  'previousDocument.*.referenceNumber': ['previousDocument.*'],
  'seller.address.streetNumber': ['seller'],
  'seller.address.postcode': ['seller'],
  'seller.contactPerson.telephone': ['seller'],
  transportDocument: ['specificCircumstanceIndicator'],
  transportEquipment: ['containerIndicator'],
  'transitCustomsOffices.*.arrivalDateAndTime': ['transitCustomsOffices.*'],
  'transitCustomsOffices.*.reEntryIndicator': ['entryIndicator'],
  'transportEquipment.*.containerIdentificationNumber': ['transportEquipment', 'transportEquipment.*', 'containerIndicator'],
  'transportEquipment.*.goodsReferences.*.containerIdentificationNumber': ['transportEquipment'],
}

const HOUSE_CONSIGNMENT_FIELDS_DEPS: Record<string, string[]> = {
  buyer: ['differentBuyer', 'differentPlaceOfDelivery', 'placeOfDelivery'],
  'buyer.address.streetNumber': ['buyer'],
  'buyer.address.postcode': ['buyer'],
  'buyer.contactPerson.telephone': ['buyer'],
  consignee: ['countryOfDestination'],
  'consignee.contactPerson': ['consignee'],
  'consignee.contactPerson.name': ['consignee'],
  'consignee.contactPerson.telephone': ['consignee'],
  'consignee.contactPerson.email': ['consignee'],
  'consignee.address': ['consignee'],
  'consignee.address.postcode': ['consignee'],
  'consignee.name': ['consignee'],
  'consignee.typeOfPerson': ['consignee'],
  consignor: ['countryOfDestination'],
  'consignor.contactPerson': ['consignor'],
  'consignor.contactPerson.name': ['consignor'],
  'consignor.contactPerson.telephone': ['consignor'],
  'consignor.contactPerson.email': ['consignor'],
  'consignor.address': ['consignor'],
  'consignor.address.postcode': ['consignor'],
  'consignor.name': ['consignor'],
  'consignor.typeOfPerson': ['consignor'],
  countriesOfRouting: ['differentCountriesOfRouting'],
  placeOfAcceptance: ['differentPlaceOfAcceptance'],
  placeOfDelivery: ['differentPlaceOfDelivery'],
  'previousDocument.*.documentType': ['previousDocument.*'],
  'previousDocument.*.procedureType': ['previousDocument.*'],
  'previousDocument.*.referenceNumber': ['previousDocument.*'],
  seller: ['differentSeller', 'differentPlaceOfDelivery', 'placeOfDelivery'],
  'seller.address.streetNumber': ['seller'],
  'seller.address.postcode': ['seller'],
  'seller.contactPerson.telephone': ['seller'],
}

const CONSIGNMENT_ITEM_FIELDS_DEPS: Record<string, string[]> = {
  'packaging.*.typeOfPackages': ['packaging.*'],
  'packaging.*.numberOfPackages': ['packaging.*'],
  'packaging.*.shippingMarks': ['packaging.*'],
  dutyAmount: ['dutyRateUnit'],
  dutyRate: ['dutyRateUnit'],
  'additionalReference.*.referenceNumber': ['additionalReference.*'],
  passiveBorderTransportMeans: ['differentPassiveBorderTransportMeans'],
  previousDocument: ['previousDocument', 'declarationType'],
  'previousDocument.*.documentType': ['previousDocument.*'],
  'previousDocument.*.procedureType': ['previousDocument.*'],
  'previousDocument.*.referenceNumber': ['previousDocument.*'],
}

const getWildcardTokens = (pattern: string, path: string) => {
  const patternParts = pattern.split('.')
  const pathParts = path.split('.')

  if (patternParts.length !== pathParts.length) return null

  const tokens : string[] = []
  for (let i = 0; i < patternParts.length; i++) {
    const patternPart = patternParts[i]
    const pathPart = pathParts[i]
    if (patternPart === '*') {
      tokens.push(pathPart)
    } else if (patternPart !== pathPart) {
      return null
    }
  }
  return tokens
}

const replaceDepsWildcardsWithTokens = (depPattern: string, tokens: string[]): string => {
  const parts = depPattern.split('.')
  const tokenIndex = 0
  for (let i = 0; i < parts.length; i++) {
    if (parts[i] === '*') {
      parts[i] = tokens[tokenIndex] ?? '*'
    }
  }

  return parts.join('.')
}

const getDepsForPath = (depsMap: Record<string, string[]>, path: string) => {
  const result = new Set<string>()

  for (const [pattern, deps] of Object.entries(depsMap)) {
    const tokens = getWildcardTokens(pattern, path)
    if (!tokens) continue

    for (const depPattern of deps) {
      if (depPattern.includes('*')) {
        result.add(replaceDepsWildcardsWithTokens(depPattern, tokens))
      } else {
        result.add(depPattern)
      }
    }

    return result
  }

  return Array.from(result)
}

const getDeps = (scope: NctsScope, paths: string[]) => {
  const result = new Set<string>()
  let depsMap = {}
  switch (scope) {
    case 'transitOperation':
      depsMap = TRANSIT_OPERATION_FIELDS_DEPS
      break
    case 'houseConsignment':
      depsMap = HOUSE_CONSIGNMENT_FIELDS_DEPS
      break
    case 'consignmentItem':
      depsMap = CONSIGNMENT_ITEM_FIELDS_DEPS
      break
    default:
      break
  }

  for (const path of paths) {
    getDepsForPath(depsMap, path).forEach((
      d,
    ) => result.add(d))
  }

  return Array.from(result)
}

interface UseClientSideFieldRequirementsContext {
  scope: NctsScope
  editorData?: DeclarationEditorData
  phase: NctsPhase
  isOptIn: boolean
  serverValidationResult?: ServerValidationResult
}

function useClientSideFieldRequirements<TFieldValues extends FieldValues>(paths: string[], ctx: UseClientSideFieldRequirementsContext) {
  const { control, getValues } = useFormContext<TFieldValues>()
  const {
    scope, editorData, serverValidationResult, phase, isOptIn,
  } = ctx
  const isEns = editorData?.declarationType === 'ENS'
  const country = editorData?.country ?? ''
  const security = editorData?.security ?? '0'
  const additionalDeclarationType = editorData?.additionalDeclarationType ?? 'A'
  const specificCircumstanceIndicator = editorData?.specificCircumstanceIndicator ?? ''

  const fieldNames = useMemo(
    () => getDeps(scope, paths) as FieldPath<TFieldValues>[],
    [scope, paths],
  )

  /**
   * `useWatch` is the re-render trigger only — its returned array must not be read positionally.
   */
  const watchedValues = useWatch({
    control,
    name: fieldNames,
  })

  const valuesMap = useMemo(() => {
    const currentValues = getValues(fieldNames)
    const map: Record<string, unknown> = {}
    fieldNames.forEach((name, index) => {
      map[name] = currentValues[index]
    })
    return map
    // watchedValues drives recomputation; getValues supplies the aligned snapshot
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldNames, watchedValues, getValues])

  return useMemo(() => {
    const result: Record<string, FieldRule> = {}
    for (const path of paths) {
      const resolveRequirement = getDeclarationFieldRequirementsResolver(scope, isEns)
      const requirement = resolveRequirement(path, {
        country,
        phase,
        isOptIn,
        security,
        additionalDeclarationType,
        specificCircumstanceIndicator,
        valuesMap,
        serverValidationResult,
      })

      result[path] = {
        requirement,
        disabled: requirement === 'forbidden',
        hidden: requirement === 'forbidden',
      }
    }

    return result
  }, [
    additionalDeclarationType,
    specificCircumstanceIndicator,
    country,
    isEns,
    isOptIn,
    paths,
    phase,
    scope,
    security,
    serverValidationResult,
    valuesMap,
  ])
}

export default useClientSideFieldRequirements
